import React, { useState, useEffect, useRef } from 'react';
import { useSearchParams } from 'react-router-dom';
import axios from 'axios';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { 
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import { ScrollArea } from '../components/ui/scroll-area';
import { Send, Bot, User, FileText, Loader2 } from 'lucide-react';
import ReactMarkdown from 'react-markdown';

const API_URL = "";

const ChatPage = () => {
  const [searchParams] = useSearchParams();
  const initialFileId = searchParams.get('file');
  
  const [files, setFiles] = useState([]);
  const [selectedFileId, setSelectedFileId] = useState(initialFileId || '');
  const [messages, setMessages] = useState([]);
  const [inputMessage, setInputMessage] = useState('');
  const [loading, setLoading] = useState(false);
  const [loadingFiles, setLoadingFiles] = useState(true);
  const messagesEndRef = useRef(null);

  useEffect(() => {
    fetchFiles();
  }, []);

  useEffect(() => {
    if (initialFileId) {
      setSelectedFileId(initialFileId);
    }
  }, [initialFileId]);

  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  const fetchFiles = async () => {
    try {
      const response = await axios.get(`${API_URL}/api/files`, {
        withCredentials: true
      });
      setFiles(response.data);
    } catch (e) {
      console.error('Error fetching files:', e);
    } finally {
      setLoadingFiles(false);
    }
  };

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  const sendMessage = async (e) => {
    e.preventDefault();
    if (!inputMessage.trim()) return;

    const userMessage = { role: 'user', content: inputMessage };
    setMessages(prev => [...prev, userMessage]);
    setInputMessage('');
    setLoading(true);

    try {
      const response = await axios.post(`${API_URL}/api/chat`, {
        message: inputMessage,
        file_id: selectedFileId || null
      }, {
        withCredentials: true
      });

      const aiMessage = { role: 'assistant', content: response.data.response };
      setMessages(prev => [...prev, aiMessage]);
    } catch (e) {
      const errorMessage = { 
        role: 'assistant', 
        content: `Error: ${e.response?.data?.detail || e.message}`,
        error: true
      };
      setMessages(prev => [...prev, errorMessage]);
    } finally {
      setLoading(false);
    }
  };

  const selectedFile = files.find(f => f.id === selectedFileId);

  return (
    <div className="p-6 h-[calc(100vh-2rem)]" data-testid="chat-page">
      <div className="h-full flex flex-col">
        {/* Header */}
        <div className="mb-4">
          <h1 className="text-3xl font-bold tracking-tight text-zinc-950">AI Data Chat</h1>
          <p className="text-zinc-500 mt-1">Ask questions about your data using AI</p>
        </div>

        {/* File Selection */}
        <Card className="border-zinc-200 bg-white mb-4">
          <CardContent className="p-4">
            <div className="flex items-center gap-4">
              <div className="flex-1">
                <label className="text-xs font-semibold tracking-[0.1em] uppercase text-zinc-500 mb-1 block">
                  Select File to Analyze
                </label>
                <Select value={selectedFileId || "none"} onValueChange={(v) => setSelectedFileId(v === "none" ? "" : v)}>
                  <SelectTrigger className="border-zinc-200" data-testid="file-select">
                    <SelectValue placeholder="Select a file (optional)" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="none">No file selected</SelectItem>
                    {files.map((file) => (
                      <SelectItem key={file.id} value={file.id}>
                        <span className="flex items-center gap-2">
                          <FileText className="w-4 h-4" />
                          {file.filename}
                        </span>
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              {selectedFile && (
                <div className="text-sm text-zinc-500">
                  {selectedFile.row_count?.toLocaleString()} rows • {selectedFile.columns?.length} columns
                </div>
              )}
            </div>
          </CardContent>
        </Card>

        {/* Chat Area */}
        <Card className="border-zinc-200 bg-white flex-1 flex flex-col overflow-hidden">
          <CardHeader className="pb-2 border-b border-zinc-200">
            <CardTitle className="text-lg font-semibold text-zinc-950 flex items-center gap-2">
              <Bot className="w-5 h-5 text-blue-700" />
              Chat with Claude
            </CardTitle>
          </CardHeader>
          <CardContent className="flex-1 flex flex-col p-0 overflow-hidden">
            {/* Messages */}
            <ScrollArea className="flex-1 p-4">
              {messages.length === 0 ? (
                <div className="h-full flex flex-col items-center justify-center text-center p-8">
                  <Bot className="w-16 h-16 text-zinc-300 mb-4" />
                  <h3 className="text-lg font-semibold text-zinc-950 mb-2">Start a Conversation</h3>
                  <p className="text-zinc-500 max-w-md">
                    {selectedFile 
                      ? `Ask me anything about "${selectedFile.filename}". I can help you analyze the data, find patterns, and provide insights.`
                      : "Select a file to analyze, or ask me general questions about data analysis."}
                  </p>
                  <div className="mt-6 space-y-2 text-sm text-zinc-400">
                    <p>Try asking:</p>
                    <ul className="space-y-1">
                      <li>"What are the key insights from this data?"</li>
                      <li>"Summarize the main trends"</li>
                      <li>"What patterns do you see?"</li>
                    </ul>
                  </div>
                </div>
              ) : (
                <div className="space-y-4">
                  {messages.map((message, index) => (
                    <div
                      key={index}
                      className={`flex gap-3 ${message.role === 'user' ? 'justify-end' : 'justify-start'}`}
                    >
                      {message.role === 'assistant' && (
                        <div className="w-8 h-8 rounded-full bg-zinc-100 flex items-center justify-center flex-shrink-0">
                          <Bot className="w-4 h-4 text-blue-700" />
                        </div>
                      )}
                      <div
                        className={`max-w-[80%] ${
                          message.role === 'user'
                            ? 'chat-bubble-user'
                            : message.error
                            ? 'chat-bubble-ai bg-red-50 text-red-600'
                            : 'chat-bubble-ai'
                        }`}
                        data-testid={`chat-message-${message.role}`}
                      >
                        {message.role === 'assistant' ? (
                          <div className="prose prose-sm max-w-none prose-zinc">
                            <ReactMarkdown>{message.content}</ReactMarkdown>
                          </div>
                        ) : (
                          <p className="whitespace-pre-wrap">{message.content}</p>
                        )}
                      </div>
                      {message.role === 'user' && (
                        <div className="w-8 h-8 rounded-full bg-blue-700 flex items-center justify-center flex-shrink-0">
                          <User className="w-4 h-4 text-white" />
                        </div>
                      )}
                    </div>
                  ))}
                  {loading && (
                    <div className="flex gap-3 justify-start">
                      <div className="w-8 h-8 rounded-full bg-zinc-100 flex items-center justify-center flex-shrink-0">
                        <Bot className="w-4 h-4 text-blue-700" />
                      </div>
                      <div className="chat-bubble-ai flex items-center gap-2">
                        <Loader2 className="w-4 h-4 animate-spin" />
                        <span>Analyzing...</span>
                      </div>
                    </div>
                  )}
                  <div ref={messagesEndRef} />
                </div>
              )}
            </ScrollArea>

            {/* Input */}
            <div className="p-4 border-t border-zinc-200">
              <form onSubmit={sendMessage} className="flex gap-2">
                <Input
                  value={inputMessage}
                  onChange={(e) => setInputMessage(e.target.value)}
                  placeholder={selectedFile ? `Ask about ${selectedFile.filename}...` : "Ask a question..."}
                  className="flex-1 border-zinc-200"
                  disabled={loading}
                  data-testid="chat-input"
                />
                <Button
                  type="submit"
                  disabled={loading || !inputMessage.trim()}
                  className="bg-blue-700 hover:bg-blue-800 text-white"
                  data-testid="send-message-button"
                >
                  <Send className="w-4 h-4" />
                </Button>
              </form>
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default ChatPage;
